/**
 * @module AliTableAnalytics/utils/barPercentPresentation
 * @description 条形条件格式 — 运行时算「条有多宽、什么颜色」（第 3 层，与数值算样式并列）。
 *
 * 【调用方】
 * - BarPercentCell：仅条形
 * - BarPercentNumericalCell：条形 + 数值（数值样式仍走 numericalConditionalFormat）
 *
 * 【数据来源】
 * - column.barPercentConfig：建列时由 applyConditionalFormatToColumn 写入
 * - row[column.code]：后端单元格对象，常见字段 value、bar_percent
 * - 交叉表行上可能有 row.metric_name.value，用于 allowList 白名单判断
 */

import { handleColorToRGBA } from '@/pages/datalab/utils/utils';

import type { TBarPercentConfig, TAliTableColumn } from '../types';

const clampPercent = (n: number): number => Math.min(100, Math.max(0, n));

/** BarPercent* 单元格组件消费的条形展示参数 */
export type TBarPercentPresentation = {
  /** 0–100，用于 fill-bar 的 width% */
  safePercent: number;
  /** 条背景色（纯色或按百分比分档的渐变色） */
  backgroundColor: string;
  /**
   * true：不画条，只显示 format 后的 text。
   * 用于交叉表 allowList：当前行指标不在白名单时不展示条形。
   */
  showPlainTextOnly: boolean;
};

/**
 * 根据当前行 + 列配置计算条形展示。
 *
 * 百分比取值：
 * - 无 allowList：用 cell.bar_percent（相对 min/max 算好的 0–100）
 * - 有 allowList：用 cell.value（交叉表场景下 value 即该格占比）
 *
 * 颜色：
 * - solidColor：取 colorTheme.color[0]
 * - gradientColor：按 safePercent 每 20% 一档取 color[bucketIndex]
 */
export const resolveBarPercentPresentation = (
  row: Record<string, unknown> | undefined,
  column: TAliTableColumn,
): TBarPercentPresentation => {
  const cell = row?.[column.code] as
    | { value?: unknown; bar_percent?: unknown }
    | undefined;
  const { colorTheme, allowList } = column.barPercentConfig || {};

  // 交叉表：行上 metric_name 标识当前指标；普通表无此字段，回退 originName 做白名单匹配
  const mv = (row?.metric_name as { value?: unknown } | undefined)?.value;
  const metricForAllowList = mv ?? column.originName;

  // 有 allowList 时 value 即交叉表占比；否则用后端按 min/max 算好的 bar_percent
  const percent = allowList?.length ? cell?.value : cell?.bar_percent;
  const safePercent = clampPercent(Number(percent ?? 0));

  // 未配置主题或色板为空时的兜底色（与面板默认条色一致）
  let backgroundColor = '#E0EAFD';
  const { type, color } = colorTheme || { color: [] };
  // 必须先判断 color[0]：空色板时 handleColorToRGBA 会对 undefined 抛错，|| 兜底无法生效
  if (type === 'solidColor' && color?.[0]) {
    backgroundColor = handleColorToRGBA(color[0]) || backgroundColor;
  } else if (type === 'gradientColor' && color?.length) {
    // 面板渐变固定 5 档（0–20%、20–40%…），bucketIndex 上限 4
    const bucketIndex = Math.min(4, Math.max(0, Math.floor(safePercent / 20)));
    // 色板少于 5 档时回退到最后一个可用色，避免高占比取到 undefined
    const idx = color[bucketIndex]
      ? bucketIndex
      : Math.min(bucketIndex, Math.max(0, color.length - 1));
    backgroundColor = handleColorToRGBA(color[idx]) || backgroundColor;
  }

  // 配置了白名单且当前行指标不在其中 → 只渲染数值，不画背景条
  const showPlainTextOnly = Boolean(
    allowList?.length &&
      (!metricForAllowList ||
        !allowList.includes(String(metricForAllowList))),
  );

  return { safePercent, backgroundColor, showPlainTextOnly };
};
